import { useParams } from 'react-router-dom';

import { useCustomLatestCompareToVersions } from 'hooks/useCustomLatestCompareToVersions';
import useHashmapConverter from 'hooks/useHashmapConverter';

import { DEFAULT_QUALITY_CATEGORY } from 'components/widgets/LogicQualityCategoriesTable/LogicQualityCategoriesTable';

/**
 * Resolves all required params by prioritizing the most reliable data source.
 *
 * Priority order:
 * 1. Explicit props passed to the component (projectId, contractId, latestVersionId)
 * 2. Latest version values (only when inside a Report context)
 * 3. URL params (used in Page mode)
 *
 * This ensures the hook always returns the correct project/contract IDs
 * and fileMetaDataId regardless of whether the component is rendered
 * inside a Report, a Page, or directly via URL navigation.
 */
export type UseResolvedParams = ReturnType<typeof useResolvedParams>;

export const useResolvedParams = ({ projectId, contractId, latestVersionId, componentKey }) => {
    //Used only in report mode to get latest version : hashCode,name and date.
    const { latestVersion } = useCustomLatestCompareToVersions({
        projectId: projectId,
        contractId: contractId,
        componentKey: componentKey,
    });

    //Get values from url, categoryUrlParam defaults to "All" in report mode if not in url
    const { id: versionHashCodeUrlParam, category: categoryUrlParam = DEFAULT_QUALITY_CATEGORY } = useParams<{
        id: string;
        category: string;
    }>();
    const { idList, versionList } = useHashmapConverter(versionHashCodeUrlParam);

    // If the widget runs inside a Report, use projectId and contractId from props;
    // otherwise (Page mode) use projectId and contractId from versionList first found object.
    const resolvedProjectId = projectId ?? versionList[0]?.projectId;
    const resolvedContractId = contractId ?? versionList[0]?.contractId;

    const fileMetaDataIdFromIdList = idList.length > 0 ? parseInt(idList.join(',')) : undefined;

    // If the widget runs inside a Report, use the version from props(latestVersionId);
    // otherwise (Page mode) use the version parsed from the URL.
    const selectedVersionId = latestVersionId ?? fileMetaDataIdFromIdList;

    const resolvedCategory = categoryUrlParam?.replace('_', '/');

    const versionName = latestVersion?.versionName ?? versionList[0]?.versionName;
    const versionDate = latestVersion?.versionDate ?? versionList[0]?.versionDate;

    return {
        versionName,
        versionDate,
        resolvedCategory,
        selectedVersionId,
        resolvedProjectId,
        resolvedContractId,
        latestVersion,
    };
};
